// ============================================
// 🩺 B252 (AI_114) — Persistência dos documentos PRÓPRIOS da enfermagem
// ============================================
// Coleção própria `documentos-enfermagem` (NÃO `prescricoes` — não é prescrição).
// A enfermeira é a autora; vínculo/autoria imutáveis nas firestore.rules; trilha
// de auditoria em edição/exclusão (espelha o padrão das coleções clínicas, B213).

import {
  collection,
  addDoc,
  updateDoc,
  deleteDoc,
  doc,
  serverTimestamp,
  query,
  where,
  onSnapshot,
  Timestamp,
} from "firebase/firestore";
import { db } from "../../../lib/firebase";
import {
  registrarEdicaoTrilha,
  registrarExclusaoTrilha,
} from "../../../shared/utils/trilhaAuditoria";
import { DadosDeclaracaoAgendamento } from "./gerarDeclaracaoAgendamento";

export const COLECAO_DOCS_ENFERMAGEM = "documentos-enfermagem";

export type TipoDocumentoEnfermagem = "declaracao_agendamento";

export interface DocumentoEnfermagem {
  id: string;
  pacienteId: string;
  tipo: TipoDocumentoEnfermagem;
  titulo: string;
  dados: DadosDeclaracaoAgendamento;
  criadoPor: string;
  criadoPorNome: string;
  // Registro (COREN) do autor, gravado p/ o PDF reimpresso mostrar o profissional
  // correto mesmo que outra pessoa reabra/imprima depois.
  criadoPorRegistro?: string;
  criadoEm?: Timestamp | null;
}

interface AutorTrilha {
  id: string;
  nome: string;
}

// Cria um documento de enfermagem. Retorna o id criado.
export async function criarDocumentoEnfermagem(params: {
  pacienteId: string;
  tipo: TipoDocumentoEnfermagem;
  titulo: string;
  dados: DadosDeclaracaoAgendamento;
  autor: AutorTrilha;
  autorRegistro?: string;
}): Promise<string> {
  const ref = await addDoc(collection(db, COLECAO_DOCS_ENFERMAGEM), {
    pacienteId: params.pacienteId,
    tipo: params.tipo,
    titulo: params.titulo,
    dados: params.dados,
    criadoPor: params.autor.id,
    criadoPorNome: params.autor.nome,
    criadoPorRegistro: params.autorRegistro || "",
    criadoEm: serverTimestamp(),
  });
  return ref.id;
}

// Atualiza os dados de um documento (edição da própria autora) + trilha.
export async function atualizarDocumentoEnfermagem(params: {
  id: string;
  pacienteId: string;
  titulo: string;
  dados: DadosDeclaracaoAgendamento;
  autor: AutorTrilha;
}): Promise<void> {
  await updateDoc(doc(db, COLECAO_DOCS_ENFERMAGEM, params.id), {
    titulo: params.titulo,
    dados: params.dados,
  });
  await registrarEdicaoTrilha({
    colecao: COLECAO_DOCS_ENFERMAGEM,
    docId: params.id,
    pacienteId: params.pacienteId,
    rotulo: params.titulo,
    camposAlterados: ["dados"],
    usuario: params.autor,
  });
}

// Exclui um documento + deixa a lápide na trilha de auditoria.
export async function excluirDocumentoEnfermagem(params: {
  id: string;
  pacienteId: string;
  rotulo: string;
  autor: AutorTrilha;
}): Promise<void> {
  await registrarExclusaoTrilha({
    colecao: COLECAO_DOCS_ENFERMAGEM,
    docId: params.id,
    pacienteId: params.pacienteId,
    rotulo: params.rotulo,
    usuario: params.autor,
  });
  await deleteDoc(doc(db, COLECAO_DOCS_ENFERMAGEM, params.id));
}

// Assina a lista de documentos do paciente (mais recentes primeiro).
// Ordenação no CLIENTE (não no Firestore) de propósito: a query só filtra por
// pacienteId — assim NÃO exige índice composto (pacienteId+criadoEm). A lista
// por paciente é pequena, então ordenar em memória é trivial. Documento
// recém-criado (criadoEm ainda pendente do servidor) vai para o topo.
export function assinarDocumentosEnfermagem(
  pacienteId: string,
  callback: (docs: DocumentoEnfermagem[]) => void
): () => void {
  const q = query(
    collection(db, COLECAO_DOCS_ENFERMAGEM),
    where("pacienteId", "==", pacienteId)
  );
  return onSnapshot(q, (snap) => {
    const lista: DocumentoEnfermagem[] = snap.docs.map((d) => ({
      id: d.id,
      ...(d.data() as Omit<DocumentoEnfermagem, "id">),
    }));
    lista.sort((a, b) => {
      const ta = a.criadoEm?.toMillis?.() ?? Infinity;
      const tb = b.criadoEm?.toMillis?.() ?? Infinity;
      return tb - ta;
    });
    callback(lista);
  });
}
